/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import {
  ShoppingCart,
  Thermostat,
  Delete,
  Science,
  EventBusy,
  Event,
  MoreHoriz,
  Description
} from '@mui/icons-material';
import DynamicRequestForm from '../../shared/requests/DynamicRequestForm';

const ICON_MAP = {
  ShoppingCart: <ShoppingCart />,
  Thermostat: <Thermostat />,
  Delete: <Delete />,
  Science: <Science />,
  EventBusy: <EventBusy />,
  Event: <Event />,
  MoreHoriz: <MoreHoriz />
};

// `selectedKey`/`onSelectKey` are optional: pass them to drive which request
// type is open from the parent, so the parent's own back button can step back to
// this type list instead of leaving the screen entirely. Left out, the selection
// is kept internally as before.
const RequestSelector = ({ project, onBack, onSubmitted, selectedKey, onSelectKey }) => {
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [internalKey, setInternalKey] = useState(null);

  const isControlled = typeof onSelectKey === 'function';
  const activeKey = isControlled ? selectedKey : internalKey;
  const selectKey = (key) => (isControlled ? onSelectKey(key) : setInternalKey(key));

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/requests/schemas')
      .then(({ data }) => { if (!cancelled) setSchemas(data); })
      .catch(err => console.error('Error loading schemas:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (activeKey) {
    return (
      <DynamicRequestForm
        schemaKey={activeKey}
        project={project}
        mode="create"
        onCancel={() => selectKey(null)}
        onSubmitted={(req) => {
          selectKey(null);
          if (onSubmitted) onSubmitted(req);
          else if (onBack) onBack();
        }}
      />
    );
  }

  if (loading) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Submit New Request
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select the type of request to submit for: <strong>{project.project_title}</strong>
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {schemas.map((s) => (
          <Grid item xs={12} md={6} key={s.key}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
              }}
              onClick={() => selectKey(s.key)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: `${s.color || 'primary'}.main`, mr: 2, display: 'flex', alignItems: 'center' }}>
                    {ICON_MAP[s.icon] || <Description />}
                  </Box>
                  <Typography variant="h6" component="h3">
                    {s.label}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {s.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" color={s.color || 'primary'}>
                  Create Request
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {onBack && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
          <Button variant="outlined" onClick={onBack}>
            Back to Project
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default RequestSelector;
